import jsPDF from "jspdf";
import QRCode from "qrcode";
import moment from "moment";
import { startCase } from "lodash";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import uuid from "@/app/infrastructures/misc/common-library/Uuid";
import { codDfodLabel, limitLengthName, normalizeDutyTaxTerms, showDutyTaxBadge } from "../../../module";
import { MultilingualText, renderMultilingualText } from "@/app/infrastructures/misc/js-pdf copy/render-multilingual-text";
import { buildDestinationLine, computeTariff, createSolidBadge, maskPhoneLast4, setFontAuto, shiftX } from "./utils";

export const renderFooterBox = async (params: {
  doc: jsPDF | any;
  baseX: number;
  headerSeparatorY: number;
  leftMargin: number;
  bookingType: string;
  accountType: string;
  data: PrintDataEntitas;
  translate: Function;
}) => {
  const doc = params.doc;
  const boxX = params.leftMargin;
  const boxY = params.headerSeparatorY - 2;
  const boxW = shiftX(params.baseX + 105) - params.leftMargin;
  const boxH = 21;
  const col1W = 36;
  const col2W = 17;
  const col3W = 25;
  const col4W = boxW - col1W - col2W - col3W;

  doc.setLineWidth(0.3);
  doc.setDrawColor("#000000");
  doc.rect(boxX, boxY, boxW, boxH);
  doc.line(boxX + col1W + col2W, boxY, boxX + col1W + col2W, boxY + boxH);
  doc.line(
    boxX + col1W + col2W + col3W + 1,
    boxY,
    boxX + col1W + col2W + col3W + 1,
    boxY + boxH
  );

  const leftColX = boxX + 1;
  const labelFontSize = 6;
  const valueFontSize = 6;
  const labelValueGap = 2.4;
  const blockGap = 5.75;

  doc.setTextColor("#333333");
  doc.setFont("Inter", "medium");
  doc.setFontSize(labelFontSize);

  const product = String(params.data.sttProductType || "");
  const footerNameMaxChars = product.toUpperCase() === "INTERPACK" ? 13 : 19;

  const renderNameWithMaskedPhone = async (name: any, phone: any, startY: number) => {
    const maskedPhone = maskPhoneLast4(phone);
    const nameUpper = String(name || "").trim().toUpperCase();

    doc.setFont("Inter", "bold");
    doc.setFontSize(valueFontSize);

    const maskText = maskedPhone ? ` ${maskedPhone}` : "";
    const maskWidth = maskText ? doc.getTextDimensions(maskText).w : 0;
    const maxNameWidth = Math.max(0, col1W - 4 - maskWidth);

    let nameLimit = footerNameMaxChars;
    let limitedName = limitLengthName(nameUpper, nameLimit);
    while (nameLimit > 0 && doc.getTextDimensions(limitedName).w > maxNameWidth) {
      nameLimit -= 1;
      limitedName = limitLengthName(nameUpper, nameLimit);
    }

    const nameWidth = doc.getTextDimensions(limitedName).w;
    await renderMultilingualText(
      new MultilingualText({
        doc,
        text: limitedName,
        defaultFont: "Inter",
        product,
        startX: leftColX,
        startY,
        maxWidth: maxNameWidth,
        fontSize: valueFontSize,
        fontStyle: "bold",
        maxLines: 1
      })
    );

    if (maskedPhone) {
      doc.setFont("Roboto Mono", "bold");
      doc.setFontSize(valueFontSize);
      doc.text(maskedPhone, leftColX + nameWidth + 1, startY);
    }
  };

  const yLabel1 = boxY + 3;
  doc.text(String(params.translate("receipt.shipper")).toUpperCase(), leftColX, yLabel1);
  await renderNameWithMaskedPhone(params.data.sttSenderName, params.data.sttSenderPhone, yLabel1 + labelValueGap);

  const yLabel2 = yLabel1 + blockGap;
  doc.setFont("Inter", "medium");
  doc.setFontSize(labelFontSize);
  doc.text(String(params.translate("receipt.receiver")).toUpperCase(), leftColX, yLabel2);
  await renderNameWithMaskedPhone(
    params.data.sttRecipientName,
    params.data.sttRecipientPhone,
    yLabel2 + labelValueGap
  );

  const yLabel3 = yLabel2 + blockGap;
  doc.setFont("Inter", "medium");
  doc.setFontSize(labelFontSize);
  doc.text(String(params.translate("receipt.destination")).toUpperCase(), leftColX, yLabel3);
  const destValue = buildDestinationLine(params.data);
  setFontAuto(doc, destValue, "bold");
  doc.setFontSize(valueFontSize);
  let destLines = doc.splitTextToSize(destValue, col1W - 4);
  if (destLines.length > 2) {
    destLines = destLines.slice(0, 2);
  }
  doc.text(destLines, leftColX, yLabel3 + labelValueGap);

  const midX = boxX + col1W;
  const createdAt = new Intl.DateTimeFormat("en-GB").format(new Date(params.data.sttCreatedAt));
  const estimateSla = moment(params.data.estimateSla).format("DD/MM/YYYY");

  doc.setFont("Inter", "medium");
  doc.setFontSize(labelFontSize);
  doc.text(String(params.translate("receipt.createdAtV2")).toUpperCase(), midX, boxY + 3);
  setFontAuto(doc, createdAt, "bold");
  doc.setFontSize(valueFontSize);
  doc.text(createdAt, midX, boxY + 5.2);

  doc.setFont("Inter", "medium");
  doc.setFontSize(labelFontSize);
  doc.text(String(params.translate("receipt.estimation")).toUpperCase(), midX, boxY + 8.2);
  setFontAuto(doc, estimateSla, "bold");
  doc.setFontSize(valueFontSize);
  doc.text(estimateSla, midX, boxY + 10.4);

  const tariffX = boxX + col1W + col2W + 2;
  const tariff = computeTariff({
    bookingType: params.bookingType,
    accountType: params.accountType,
    data: params.data
  });
  const tariffTitle =
    codDfodLabel(params.data.sttIsCod, params.data.sttIsDfod) === "dfod"
      ? "DFOD"
      : codDfodLabel(params.data.sttIsCod, params.data.sttIsDfod) === "cod"
      ? "COD"
      : startCase(params.translate("receipt.totalCost"));
  doc.setFont("Inter", "medium");
  doc.setFontSize(7);
  doc.text(tariffTitle, tariffX, boxY + 3);
  setFontAuto(doc, String(tariff.totalBiaya), "bold");
  doc.setFontSize(9);
  doc.text(String(tariff.totalBiaya), tariffX, boxY + 7);
  doc.setFont("Inter", "bold");
  doc.setFontSize(5);
  let feeY = boxY + 10.6;
  doc.text(String(params.translate("receipt.shippingCostV2")), tariffX, feeY);
  doc.text(":", tariffX + 12.5, feeY);
  setFontAuto(doc, String(tariff.totalBiayaKirim), "semibold");
  doc.text(String(tariff.totalBiayaKirim), tariffX + 13.5, feeY);
  feeY += 2.6;
  doc.setFont("Inter", "bold");
  doc.text(String(params.translate("receipt.othersV2")), tariffX, feeY);
  doc.text(":", tariffX + 12.5, feeY);
  setFontAuto(doc, String(tariff.lainLain), "semibold");
  doc.text(String(tariff.lainLain), tariffX + 13.5, feeY);

  if (!tariff.isEstimationBound && tariff.showReturnFee) {
    feeY += 2.6;
    doc.setFont("Inter", "bold");
    doc.text(String(params.translate("receipt.returnFeeV3")), tariffX, feeY);
    doc.text(":", tariffX + 12.5, feeY);
    setFontAuto(doc, String(tariff.returnFee), "semibold");
    doc.text(String(tariff.returnFee), tariffX + 13.5, feeY);
  }

  if (tariff.showPajak) {
    feeY += 2.6;
    doc.setFont("Inter", "bold");
    doc.text(String(params.translate("receipt.estTax")), tariffX, feeY);
    doc.text(":", tariffX + 12.5, feeY);
    setFontAuto(doc, String(tariff.pajak), "semibold");
    doc.text(String(tariff.pajak), tariffX + 13.5, feeY);
  }

  if (showDutyTaxBadge(params.data)) {
    const dutyTerms = normalizeDutyTaxTerms(params.data.sttDutyTaxTerms);
    if (dutyTerms) {
      createSolidBadge(params.doc, dutyTerms, tariffX, feeY + 2, 7);
    }
  }

  const qrX = boxX + col1W + col2W + col3W;
  doc.setFillColor(0);
  doc.rect(qrX + 1.2, boxY + 0.3, col4W - 1, boxH - 0.6, "F");
  const background = await import("@/app/ui/assets/images/bg-qr-new-resi.png");
  const bgW = 21.8;
  const bgH = 6;
  const bgX = qrX + 0.5 + (col4W - bgW) / 2;
  const bgY = 124.2;
  doc.addImage(background.default, "PNG", bgX, bgY, bgW, bgH, undefined, "FAST");

  doc.setTextColor("#000000");
  doc.setFont("Inter", "extrabold");
  doc.setFontSize(6);
  const translatedTrackPackageHere = String(params.translate("receipt.trackPackageHere"));
  const trackPackageHere =
    translatedTrackPackageHere === "receipt.trackPackageHere"
      ? "Lacak Paketmu\nDisini"
      : translatedTrackPackageHere;
  const trackPackageLines = trackPackageHere.split("\n");
  const trackLine1 = trackPackageLines[0] || "Lacak Paketmu";
  const trackLine2 = trackPackageLines[1] || "Disini";
  doc.text(trackLine1, qrX + col4W / 2, boxY + 3, { align: "center" });
  doc.text(trackLine2, qrX + col4W / 2, boxY + 4.8, { align: "center" });
  doc.setTextColor("#333333");

  const qrcodeValue = `${process.env.VUE_APP_WEBSITE_LION_PARCEL}/track/stt?q=${params.data.sttNo}`;
  const qrcode = await QRCode.toDataURL(qrcodeValue || "", { errorCorrectionLevel: "Q" });
  const qrSize = Math.min(col4W - 2, boxH - 8);
  const qrTop = boxY + 8;
  const qrLeft = qrX + 0.5 + (col4W - qrSize) / 2;
  doc.addImage(qrcode, "PNG", qrLeft, qrTop, qrSize, qrSize - 2, `qr-${uuid()}`, "FAST");
  doc.setLineWidth(0.6);

  return {
    boxY,
    boxH
  };
};

